const consentRegions = new Set('AT BE BG HR CY CZ DK EE FI FR DE GR HU IS IE IT LV LI LT LU MT NL NO PL PT RO SK SI ES SE GB CH'.split(' '));
const optOutKey = 'bestdrive_advertising_opt_out';
export const consentKey = 'bestdrive_cookie_choices_v1';
export const consentNotice = {
  version: '2026-10-21-v1',
  text: 'Essential cookies keep sign-in and enquiries working. Optional analytics measures website use. Optional advertising shares browser/device information, network information and advertising cookie/click identifiers with Google Ads to measure successful enquiries and optimise campaigns. No enquiry contact details or customer matching are shared. You can reject optional cookies or change your choices at any time.',
  choices: ['Accept', 'Reject', 'Manage'],
  recipient: 'Google Ads',
};
export interface CookieChoice { visitor: string; at: string; noticeVersion: string; region: string | null; analytics: boolean; advertising: boolean }
let visitRegion: string | null = null;
let resolved = false;
let initialization: Promise<void> | undefined;
let loaded = false;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach(listener => listener());
export function subscribeConsent(listener: () => void): () => void { listeners.add(listener); return () => { listeners.delete(listener); }; }
export function isAdvertisingRegionAllowed(region: string | null): boolean {
  return !!region && /^[A-Z]{2}$/.test(region) && !['XX', 'T1'].includes(region) && !consentRegions.has(region);
}
export function readCookieChoice(): CookieChoice | null {
  try {
    const choice = JSON.parse(localStorage.getItem(consentKey) || 'null');
    return choice && choice.noticeVersion === consentNotice.version && typeof choice.advertising === 'boolean' && typeof choice.analytics === 'boolean' && typeof choice.visitor === 'string' && typeof choice.at === 'string' ? choice : null;
  } catch { return null; }
}
function hasOptOut(): boolean {
  try { return localStorage.getItem(optOutKey) === 'denied' || navigator.globalPrivacyControl === true || navigator.doNotTrack === '1'; }
  catch { return true; }
}
/** Private offer links carry a secret token in the URL; advertising must never see them. */
export function isPrivateAdvertisingRoute(path = typeof location === 'undefined' ? '' : location.pathname): boolean {
  return /^\/offer(\/|$)/.test(path);
}
export function canSendAdvertising(): boolean {
  if (isPrivateAdvertisingRoute()) return false;
  const choice = readCookieChoice();
  return !hasOptOut() && (choice ? choice.advertising : resolved && isAdvertisingRegionAllowed(visitRegion));
}
export function shouldShowConsentBanner(): boolean { return resolved && !readCookieChoice() && !isAdvertisingRegionAllowed(visitRegion); }
export function canSendAnalytics(): boolean { return readCookieChoice()?.analytics === true; }
function queue(..._args: unknown[]): void {
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer?.push(arguments);
}
function clearAdvertisingCookies(): void {
  const names = document.cookie.split(';').map(item => item.split('=')[0]?.trim()).filter(name => name && /^(_gcl_|_ga|_gid|_gat)/.test(name));
  const host = location.hostname.split('.');
  const domains = ['', ...host.map((_, i) => '.' + host.slice(i).join('.'))];
  for (const name of names) for (const domain of domains) document.cookie = `${name}=; Max-Age=0; path=/${domain ? '; domain=' + domain : ''}`;
}
function applyConsent(): void {
  const advertising = canSendAdvertising();
  // Acceptance covers ordinary Ads measurement, never enhanced matching or personalised ads.
  queue('consent', 'update', { ad_storage: advertising ? 'granted' : 'denied', analytics_storage: canSendAnalytics() ? 'granted' : 'denied', ad_user_data: advertising ? 'granted' : 'denied', ad_personalization: 'denied' });
  if (!advertising) {
    // Remove queued event commands, never replay conversions after a withdrawal.
    const layer = window.dataLayer || [];
    for (let i = layer.length - 1; i >= 0; i--) {
      const item = layer[i];
      if (item && typeof item === 'object' && (('0' in item && item[0] === 'event') || ('event' in item))) layer.splice(i, 1);
    }
    // Private routes only pause advertising; they must not erase a visitor's saved choice or cookies.
    if (!isPrivateAdvertisingRoute()) clearAdvertisingCookies();
  }
  if (advertising && !loaded) {
    loaded = true;
    queue('set', 'ads_data_redaction', true);
    queue('js', new Date());
    queue('config', 'AW-18310421880', { allow_ad_personalization_signals: false });
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://www.googletagmanager.com/gtag/js?id=AW-18310421880';
    document.head.appendChild(script);
  }
  notify();
}
export function saveCookieChoice(analytics: boolean, advertising: boolean): void {
  const previous = readCookieChoice();
  const choice: CookieChoice = { visitor: previous?.visitor || crypto.randomUUID(), at: new Date().toISOString(), noticeVersion: consentNotice.version, region: visitRegion, analytics, advertising };
  // Append evidence; withdrawal must not overwrite the original acceptance.
  const history = JSON.parse(localStorage.getItem('bestdrive_cookie_history_v1') || '[]');
  localStorage.setItem('bestdrive_cookie_history_v1', JSON.stringify([...history, { ...choice, notice: consentNotice }]));
  localStorage.setItem(consentKey, JSON.stringify(choice));
  localStorage.setItem(optOutKey, advertising ? 'allowed' : 'denied');
  applyConsent();
}
export function withdrawAdvertising(): void { saveCookieChoice(readCookieChoice()?.analytics || false, false); }
export function openCookieSettings(): void { window.dispatchEvent(new Event('bestdrive-open-cookie-settings')); }
export function recordAdvertisingConversion(): void {
  if (!canSendAdvertising()) return;
  try {
    const records = JSON.parse(localStorage.getItem('bestdrive_ad_conversion_choices_v1') || '[]');
    localStorage.setItem('bestdrive_ad_conversion_choices_v1', JSON.stringify([...records, { event: 'course_enquiry_success', at: new Date().toISOString(), choice: readCookieChoice(), region: visitRegion }]));
  } catch { /* Tracking must never break an enquiry. */ }
}
export function initializeAdvertising(): Promise<void> {
  if (initialization) return initialization;
  window.dataLayer = window.dataLayer || [];
  queue('consent', 'default', { ad_storage: 'denied', analytics_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied' });
  window.gtag = (...args: unknown[]) => {
    if (args[0] === 'consent') queue(...args);
    else if (canSendAdvertising()) queue(...args);
  };
  window.addEventListener('storage', e => { if ([consentKey, optOutKey, null].includes(e.key)) applyConsent(); });
  document.addEventListener('click', e => {
    const anchor = e.target instanceof Element ? e.target.closest('a[href^="tel:"], a[href^="sms:"]') : null;
    if (anchor && canSendAdvertising()) window.gtag?.('event', 'conversion', { send_to: 'AW-18310421880/0jVBCMqXldAcEPi6i5tE' });
  }, true);
  initialization = (async () => {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 2000);
    try {
      const response = await fetch('/cdn-cgi/trace', { signal: controller.signal });
      if (response.ok) visitRegion = (await response.text()).match(/^loc=([A-Z]{2})$/m)?.[1] || null;
    } catch { visitRegion = null; }
    finally { window.clearTimeout(timer); resolved = true; }
    applyConsent();
  })();
  return initialization;
}
declare global { interface Navigator { globalPrivacyControl?: boolean } }
